/**
 * عرض وتجميع «التأخرات» وخصم الغياب مع مراعاة الإجازات المعتمدة (قبول = 2).
 */

export function rowHasAnyAttendanceStamp(record) {
  if (!record) return false;
  const hasIn = (v) => v != null && String(v).trim() !== "";
  if (hasIn(record.attendance_time) || hasIn(record.leave_time)) return true;
  const extra = record.extra_attendance;
  if (!Array.isArray(extra)) return false;
  return extra.some((seg) => hasIn(seg?.in) || hasIn(seg?.out));
}

/**
 * خصم يوم غير مكتمل أو بلا بصمة مع خصم رصيد الإجازة المعتمدة من الشؤون.
 * بلا بصمة: دائماً يُطبَّق خصم الإجازة قبل خصم الباكند الكامل.
 * مع بصمة ناقصة: يُحتفظ بخصم الباكند (تأخر/جزء) ما لم تغطِّ الإجازة اليوم بالكامل.
 */
export function resolveDayDiscountWithApprovedVacations({
  fullDayGross,
  backendDiscount = 0,
  totalApprovedVacSecs = 0,
  vacationCredit = 0,
  hasAnyAttendanceStamp = false,
}) {
  const gross = Number(fullDayGross) || 0;
  const backend = Number(backendDiscount) || 0;
  const vacSecs = Number(totalApprovedVacSecs) || 0;
  const credit = Number(vacationCredit) || 0;
  const vacAdjusted =
    vacSecs > 0 ? Math.max(0, gross - credit) : null;

  if (!hasAnyAttendanceStamp) {
    if (vacAdjusted != null) return vacAdjusted;
    if (backend > 0) return backend;
    return gross;
  }

  if (vacAdjusted != null && vacAdjusted <= 0.0001) return 0;
  if (backend > 0) return backend;
  if (vacAdjusted != null) return vacAdjusted;
  return gross;
}

/** إجازة معتمدة تغطي يوم الدوام بالكامل — لا يُحسب غياب */
export function isFullApprovedVacationDay({
  totalApprovedVacSecs = 0,
  workDaySecs = 0,
  finalDiscount = 0,
}) {
  const vacSecs = Number(totalApprovedVacSecs) || 0;
  const workSecs = Number(workDaySecs) || 0;
  const discount = Number(finalDiscount) || 0;
  if (vacSecs <= 0) return false;
  if (discount <= 0.0001) return true;
  return workSecs > 0 && vacSecs >= workSecs - 60;
}

function parseTimeToSeconds(timeStr) {
  if (!timeStr || timeStr === "0") return 0;
  const parts = String(timeStr).split(":");
  if (parts.length < 2) return 0;
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  return h * 3600 + m * 60 + s;
}

export function secondsToLateHms(totalSeconds) {
  const secs = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

/** هل lateTime يمثل بديل «يوم كامل» وليس تأخرًا فعليًا من السجل */
export function isPlaceholderFullShiftLate(lateSecs, requiredDaySecs) {
  const req = Number(requiredDaySecs) || 0;
  if (req <= 0 || lateSecs <= 0) return false;
  return Math.abs(lateSecs - req) < 60;
}

export function getMinutePriceForDay({ salary, requiredDailyMinutes }) {
  const safeSalary = Number(salary) || 0;
  const dayMin = Number(requiredDailyMinutes) > 0 ? Number(requiredDailyMinutes) : 7 * 60;
  const monthlyRequiredMinutes = dayMin * 30;
  return monthlyRequiredMinutes > 0 ? safeSalary / monthlyRequiredMinutes : 0;
}

/**
 * ثواني التأخر المعروضة/المجمّعة لليوم.
 * - دوام مكتمل: lateTime من السجل.
 * - بصمة ناقصة: لا تُحسب 7:00 وهمية؛ تُستخدم lateTime الفعلي إن وُجد، وإلا المكافئ من مبلغ الخصم.
 */
export function getDisplayedDayLateSeconds(
  record,
  {
    recordQualifiesAsCompleteAttendance,
    getDisplayedDayDiscount,
    dailyDurations,
    requiredDailyMinutes,
    salary,
  },
) {
  if (!record) return 0;

  const requiredDaySecs =
    Number(dailyDurations?.[record.date]) > 0
      ? Number(dailyDurations[record.date])
      : (Number(requiredDailyMinutes) || 7 * 60) * 60;

  const lateSecs = parseTimeToSeconds(record.lateTime);

  if (recordQualifiesAsCompleteAttendance(record)) {
    return lateSecs;
  }

  if (
    lateSecs > 0 &&
    !isPlaceholderFullShiftLate(lateSecs, requiredDaySecs)
  ) {
    return lateSecs;
  }

  const discount = Number(getDisplayedDayDiscount(record)) || 0;
  if (discount <= 0) return 0;

  const minutePrice = getMinutePriceForDay({
    salary,
    requiredDailyMinutes,
  });
  if (minutePrice <= 0) return 0;
  const lateMinutes = Math.round(discount / minutePrice);
  return lateMinutes * 60;
}
